import axios, {Axios, AxiosError, AxiosInstance, AxiosRequestConfig} from "axios";
import {apiUrl, domainUrl} from "./apiUrl";
import {ServerExceptionModel} from "../model/core/ServerExceptionModel";
import {getLocaleFromLocal, setLocalIpInHeaders} from "../core/localization/localizationHelper";
import {isBlank} from "../helpers/stringHelper";

const qs = require("qs");

export interface MinoAxios extends AxiosInstance {
    onError: { [key: string]: (error: any) => void }

}


const getToken = () => {
    const ls = localStorage.getItem('token');
    if (ls !== null) {
        return ls as string;
    }
    return '' as string;
};
const getRefreshToken = () => {
    const ls = localStorage.getItem('refreshToken');
    if (ls !== null) {
        return ls as string;
    }
    return '' as string;
};
// const setLocalIpInHeaders = (config: any) => {
//     let localIp = getLocalIpSync();
//     if (localIp === "undefined") {
//         setLocalIpAsync();
//     }
//     config.headers!["localIp"] = localIp;
// };

const timeoutValue = 90000;
const api = axios.create({
    baseURL: domainUrl,
    timeout: 600000,
    withCredentials: true,
    paramsSerializer: function (params) {
        return qs.stringify(params, {
            encode: false,
        })
    },

}) as MinoAxios;


const fixConfig = (axios: Axios, config: AxiosRequestConfig) => {
    const token = getToken();
    if (!!token) {
        config.headers!["Authorization"] = `Bearer ${token}`;
    }
    if ((axios.defaults as any).agent === (config as any).agent) {
        delete (config as any).agent;
    }
    if (axios.defaults.httpAgent === config.httpAgent) {
        delete config.httpAgent;
    }
    if (axios.defaults.httpsAgent === config.httpsAgent) {
        delete config.httpsAgent;
    }
    if(config.timeout === undefined ||config.timeout < timeoutValue){
        config.timeout = timeoutValue // 70 saniye;
    }

}

api.onError = {};

// Süresi dolan erişim token'ı tek seferde yenilenir: aynı anda düşen istekler aynı yenilemeyi bekler, diğer sekmelerle
// tarayıcı kilidi (navigator.locks; yalnız https'te var) üzerinden sıraya girilir. Kilit yoksa da sunucu aynı anda
// gelen yenilemelere kısa bir tolerans tanır.
// "rejected": sunucu yenileme token'ını reddetti (oturum bitti); "transient": ağ/sunucu hatası, token'lar korunur
type RefreshResult = "ok" | "rejected" | "transient";
let refreshInFlight: Promise<RefreshResult> | undefined;
const refreshAccessToken = (expiredToken: string): Promise<RefreshResult> => {
    if (!refreshInFlight) {
        const run = async (): Promise<RefreshResult> => {
            // Başka bir sekme bu arada yenilediyse onun token'ı kullanılır
            const currentToken = getToken();
            if (!isBlank(currentToken) && currentToken !== expiredToken) {
                return "ok";
            }
            const refreshToken = getRefreshToken();
            if (isBlank(refreshToken)) {
                return "rejected";
            }
            // Geçici hatada bir kez hemen tekrar denenir (sunucu yenilemeyi yapıp cevap kaybolduysa ikinci deneme
            // aynı geçerli token'ı alır)
            for (let attempt = 0; attempt < 2; attempt++) {
                try {
                    // Yenileme token'ı adreste (sunucu kayıtlarında) görünmesin diye gövdede gönderilir
                    const response = await axios.create({baseURL: domainUrl, timeout: 30000, withCredentials: true})
                        .post(apiUrl.auth.userLogin + "refreshToken", {refreshToken});
                    const data = response?.data?.data;
                    if (response.status === 200 && data?.accessToken && data?.refreshToken) {
                        // Yalnız gönderilen token hâlâ duruyorsa yazılır: başka sekmede başka oturum açıldıysa ezilmez,
                        // çıkış yapıldıysa (depo boş) oturum geri getirilmez
                        if (getRefreshToken() !== refreshToken) {
                            return isBlank(getRefreshToken()) ? "rejected" : "ok";
                        }
                        localStorage.setItem("refreshToken", data.refreshToken);
                        localStorage.setItem("token", data.accessToken);
                        return "ok";
                    }
                } catch (e) {
                    const status = (e as AxiosError)?.response?.status;
                    const code = ((e as AxiosError)?.response?.data as any)?.exception?.simpleCode as string | undefined;
                    if (status === 401 && code?.startsWith("AUTHENTICATION")) {
                        // Yalnız gönderilen token hâlâ duruyorsa silinir (başka sekme yenilemiş ya da giriş yapmış olabilir)
                        if (getRefreshToken() === refreshToken) {
                            localStorage.removeItem("refreshToken");
                            return "rejected";
                        }
                        return "ok";
                    }
                    console.log(e);
                }
            }
            // Sunucu yeniden başlıyor, ağ koptu, 5xx: oturum geçerli olabilir, token'lar silinmez
            return "transient";
        };
        const locks = (navigator as any)?.locks;
        const promise: Promise<RefreshResult> = locks?.request ? locks.request("ratech-token-refresh", run) : run();
        refreshInFlight = promise.finally(() => {
            refreshInFlight = undefined;
        });
    }
    return refreshInFlight;
};


api.interceptors.request.use(
    function (config) {
        const token = getToken();
        if (!!token) {
            config.headers!["Authorization"] = `Bearer ${token}`;
        }
        config.withCredentials = true;

        config.headers!["Accept-Language"] = getLocaleFromLocal();
        setLocalIpInHeaders(config);
        if (config.method?.toLowerCase() === "post" || config.method?.toLowerCase() === "put") {
            config.headers!["Content-Type"] = "application/json";
        }

        if(config.timeout === undefined ||config.timeout < timeoutValue){
            config.timeout = timeoutValue // 70 saniye;
        }
        return config;
    }, function (error) {
        return Promise.reject(error)
    }
);

api.interceptors.response.use(
    (response) => {
        if (response.data.exception) {
            throw response.data.exception
        }

        return response
    },
    (error: AxiosError) => {
        const {config, response} = error;
        if (!config) {
            return Promise.reject(error);
        }
        if (response?.status === 401) {
            const errorMessage = (response?.data as any)?.exception as ServerExceptionModel
            if (errorMessage?.simpleCode?.startsWith?.("AUTHENTICATION")) {
                const numberError = Number(errorMessage.simpleCode.replace("AUTHENTICATION",""));
                if (errorMessage.simpleCode === "AUTHENTICATION4") {
                    config.transformRequest = [(data) => data];
                    const expiredToken = ((config.headers as any)?.["Authorization"] ?? "").replace("Bearer ", "");
                    return refreshAccessToken(expiredToken).then(result => {
                        if (result === "ok") {
                            fixConfig(api, config);
                            // Tekrar edilen istek hata verirse çağıran tarafa diğer hatalarla aynı biçimde (cevap) döner
                            return axios(config).catch(err => Promise.reject(err?.response ?? err));
                        }
                        if (result === "rejected") {
                            // Oturum bitti: süresi dolmuş token da silinir (yoksa yeniden yüklemede giriş ekranı yerine
                            // aynı döngüye girilir), giriş ekranına dönülür
                            if (getToken() === expiredToken) {
                                localStorage.removeItem("token");
                            }
                            window.location.reload();
                        }
                        return Promise.reject(error.response);
                    });
                }else if(1<=numberError && numberError <=9){

                    if(response.request.responseURL.includes("his-services")){
                        localStorage.removeItem("token");
                        window.location.reload();
                    }else{
                    }

                }
                else {

                }

            }
        }

        const events = Object.keys(api.onError);
        events.forEach(value => {
            if (api.onError[value] !== undefined) {
                api.onError[value](error);
            }
        })
        return Promise.reject(error.response)
    }
);

export default api;